import React from "react";

import { Refresh } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DIAGNOSTICS_MAX_HOURS,
  rangeHours,
  type TelemetryChartMetric,
  type TelemetryEventKind,
} from "@/lib/telemetry";
import { CHART_METRICS, metricName } from "@/components/telemetry/SeriesCharts";
import { useT } from "@/app/localization";
import {
  formatSeconds,
  fromLocalInputValue,
  toLocalInputValue,
  type TelemetryTz,
} from "@/components/telemetry/format";

/** Ids are URL values (`?preset=`); labels come from the catalog by `labelValue`. */
export const PRESETS = [
  { id: "1h", labelValue: "h1", hours: 1 },
  { id: "6h", labelValue: "h6", hours: 6 },
  { id: "24h", labelValue: "h24", hours: 24 },
  { id: "48h", labelValue: "h48", hours: 48 },
  { id: "7d", labelValue: "d7", hours: 168 },
] as const;

export type PresetId = (typeof PRESETS)[number]["id"] | "custom";

/** Event-kind toggles in display order; labels from operator.telemetry.kind_filter. */
export const EVENT_KINDS: TelemetryEventKind[] = ["state_change", "gap", "command", "anomaly", "sample"];

/** Re-exported so the control bar and the charts can never drift apart. */
export const METRICS: TelemetryChartMetric[] = CHART_METRICS;

export interface TelemetryControlsProps {
  unitId: string;
  preset: PresetId;
  from: string;
  to: string;
  kinds: TelemetryEventKind[];
  metrics: TelemetryChartMetric[];
  tz: TelemetryTz;
  isFetching: boolean;
  onPreset: (preset: PresetId) => void;
  onRange: (range: { from: string; to: string }) => void;
  onKinds: (kinds: TelemetryEventKind[]) => void;
  onMetrics: (metrics: TelemetryChartMetric[]) => void;
  onTz: (tz: TelemetryTz) => void;
  onRefresh: () => void;
}

/**
 * Range and unit choose the backend window. Event/metric toggles are view
 * filters over that window, so support can explore without paying a new Athena
 * scan per click. "All samples" is the exception: it asks for a heavier scan.
 *
 * There is no auto-refresh anywhere in this feature: each refresh is a paid
 * Athena scan, and a background interval on a support tab left open overnight
 * is a bill, not a convenience.
 */
export function TelemetryControls({
  unitId,
  preset,
  from,
  to,
  kinds,
  metrics,
  tz,
  isFetching,
  onPreset,
  onRange,
  onKinds,
  onMetrics,
  onTz,
  onRefresh,
}: TelemetryControlsProps) {
  const t = useT();
  const hours = from && to ? rangeHours(from, to) : 0;
  const wide = hours > DIAGNOSTICS_MAX_HOURS;

  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-end gap-3">
        {/* The charger is the page, not a control. This panel is mounted under
            /unit/<id>, so a second way to change which unit is being read could
            only ever disagree with the route — and typing an id into it bought a
            fresh Athena scan for whatever was typed. */}
        <div>
          <Label>{t("operator.telemetry.charger")}</Label>
          <div
            className="mt-1 font-mono text-sm leading-9"
            data-testid="telemetry-unit-id"
          >
            {unitId}
          </div>
        </div>

        <div>
          <Label>{t("operator.telemetry.range")}</Label>
          <ToggleGroup
            className="mt-1"
            type="single"
            size="sm"
            variant="outline"
            value={preset}
            onValueChange={(value) => value && onPreset(value as PresetId)}
          >
            {PRESETS.map((p) => (
              <ToggleGroupItem key={p.id} value={p.id}>
                {t("operator.telemetry.preset", { preset: p.labelValue })}
              </ToggleGroupItem>
            ))}
            <ToggleGroupItem value="custom">{t("operator.telemetry.preset_custom")}</ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div>
          <Label>{t("operator.telemetry.times")}</Label>
          <ToggleGroup
            className="mt-1"
            type="single"
            size="sm"
            variant="outline"
            value={tz}
            onValueChange={(value) => value && onTz(value as TelemetryTz)}
          >
            <ToggleGroupItem value="local">{t("operator.telemetry.times_local")}</ToggleGroupItem>
            <ToggleGroupItem value="utc">{t("operator.telemetry.times_utc")}</ToggleGroupItem>
          </ToggleGroup>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={onRefresh}
          disabled={isFetching}
          data-testid="telemetry-refresh"
        >
          <Refresh className="h-4 w-4" /> {isFetching ? t("operator.telemetry.querying") : t("common.action.refresh")}
        </Button>
      </div>

      {preset === "custom" && (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="telemetry-from">{t("operator.telemetry.from_local")}</Label>
            <Input
              id="telemetry-from"
              type="datetime-local"
              className="mt-1 w-[220px]"
              value={toLocalInputValue(from)}
              onChange={(e) => {
                const iso = fromLocalInputValue(e.target.value);
                if (iso) onRange({ from: iso, to });
              }}
            />
          </div>
          <div>
            <Label htmlFor="telemetry-to">{t("operator.telemetry.to_local")}</Label>
            <Input
              id="telemetry-to"
              type="datetime-local"
              className="mt-1 w-[220px]"
              value={toLocalInputValue(to)}
              onChange={(e) => {
                const iso = fromLocalInputValue(e.target.value);
                if (iso) onRange({ from, to: iso });
              }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div>
          <Label>{t("operator.telemetry.events")}</Label>
          <ToggleGroup
            className="mt-1 flex-wrap"
            type="multiple"
            size="sm"
            variant="outline"
            value={kinds}
            onValueChange={(value) => onKinds((value as TelemetryEventKind[]) ?? [])}
          >
            {EVENT_KINDS.map((kind) => (
              <ToggleGroupItem key={kind} value={kind}>
                {t("operator.telemetry.kind_filter", { kind })}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div>
          <Label>{t("operator.telemetry.metrics")}</Label>
          <ToggleGroup
            className="mt-1 flex-wrap"
            type="multiple"
            size="sm"
            variant="outline"
            value={metrics}
            onValueChange={(value) => onMetrics((value as TelemetryChartMetric[]) ?? [])}
          >
            {METRICS.map((metric) => (
              <ToggleGroupItem key={metric} value={metric}>
                {metricName(t, metric)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div className="space-y-1 text-[11px] text-muted-foreground">
        <div data-testid="telemetry-filter-note">
          {t("operator.telemetry.filter_note")}
        </div>
        <div>
          {wide
            ? t("operator.telemetry.window_wide", { window: formatSeconds(hours * 3600), limit: DIAGNOSTICS_MAX_HOURS })
            : t("operator.telemetry.window", { window: formatSeconds(hours * 3600) })}
        </div>
        {kinds.includes("sample") && (
          <div data-testid="telemetry-samples-note">
            {t("operator.telemetry.samples_note")}
          </div>
        )}
      </div>
    </div>
  );
}
